/**
 * İZLEME EKRANI — merkezin bu araçlar için nerede olduğunu gösterir.
 *
 * NİÇİN AYRI BİR EKRAN: bir araç ilk kez eklendiğinde güncel fiyatı saniyeler
 * içinde geliyor ama on yıllık geçmiş ARKADA doluyor ve bu dakikalar sürebilir.
 * Kullanıcı o arada raporun niçin eksik olduğunu bir yerden okumak zorunda;
 * okumazsa "uygulama yanlış hesaplıyor" diye düşünür. Bu ekran o yeri veriyor.
 *
 * EKRANIN UYMAK ZORUNDA OLDUĞU İKİ KURAL:
 *  1. Dolmamış dönem için SAYI GÖSTERİLMEZ. "Eksik veri" ile "değer sıfır"
 *     ayrı şeyler; sıfır göstermek raporun sessizce yanlış olması demek.
 *  2. Merkez erişilemezse ekran ÇÖKMEZ. Cihazdaki önbellek zarfı (araç başına
 *     `cachedFrom`/`cachedTo`/`barCount`) merkeze hiç bağlanmadan okunuyor;
 *     merkez yoksa yalnızca "merkezin nerede olduğu" bilinmez ve bu görünür
 *     kılınır. Uçak modunda bu ekran hâlâ işe yarar.
 *
 * KÂR/ZARAR YOK: fiyat elde olsa bile değerleme yapılmıyor. Çok para birimli
 * değerleme, FIFO ve şirket olayları Aşama 3'ün işi; burada yapılan tek şey
 * fiyatın ELDE OLDUĞUNU söylemek.
 */

import { useCallback, useMemo } from "react";
import type { InstrumentId } from "@parafon/contracts";
import { M, YEREL } from "../dil";
import { useCentral } from "../central/CentralContext";
import type { InstrumentCacheEntry, SyncReport } from "@parafon/contracts/device-api";
import { useEsitleme } from "../central/EsitlemeSaglayici";
import type { EsitlemeKaydi } from "../central/EsitlemeSaglayici";
import { ESITLEME_SEBEBI_METNI } from "../central/esitleyici";
import { izlenenAracKimlikleri } from "../lib/esitlemeHedefi";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync } from "../repo/useAsync";
import {
  MISSING_REASON_LABEL,
  backfillProgress,
  describeTrackState,
} from "../lib/trackState";
import type { StateTone } from "../lib/trackState";
import {
  Button,
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  Notice,
  Screen,
  Tag,
  cx,
} from "../ui/primitives";

const TONE_CLASS: Readonly<Record<StateTone, string>> = {
  hazir: "border-line bg-surface text-ink",
  hazirlaniyor: "border-line bg-surface text-muted",
  durdu: "border-line bg-surface text-muted",
  elle: "border-warn/40 bg-warn/10 text-ink",
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function TrackingScreen() {
  const { repo, revision } = useRepositoryContext();
  const central = useCentral();

  /**
   * İZLENEN ARAÇ LİSTESİ HAREKET DEFTERİNDEN ÇIKIYOR, önbellekten değil.
   * Sebebi: önbellek silinebilir (kota baskısı, tarayıcı temizliği) ama defter
   * kullanıcının verisi. Listeyi önbellekten üretmek, önbellek silindiğinde
   * "hiç izlenen aracınız yok" demek olurdu — oysa hareketler yerinde duruyor.
   */
  const txs = useAsync(
    useCallback(() => repo.transactions.query({ limit: 5000 }), [repo]),
    [repo, revision],
  ).state;

  const trackedIds = useMemo<readonly InstrumentId[]>(
    () => izlenenAracKimlikleri(txs.data?.rows ?? []),
    [txs.data],
  );

  const cache = central.prices;
  const states = useAsync(
    useCallback(async (): Promise<readonly InstrumentCacheEntry[]> => {
      if (cache === null || trackedIds.length === 0) return [];
      return cache.instrumentStates(trackedIds);
    }, [cache, trackedIds]),
    [cache, central.revision, trackedIds],
  ).state;

  return (
    <Screen
      title={M.izleme.ekran.baslik}
      description={M.izleme.ekran.aciklama}
      toolbar={<SyncButton ids={trackedIds} />}
    >
      {central.isFake ? (
        <Notice tone="warn" title={M.izleme.ekran.sahteBaslik}>
          {M.izleme.ekran.sahteOnce}
          <code>?sahte=1</code>
          {M.izleme.ekran.sahteSonra}
        </Notice>
      ) : null}

      {central.unavailable !== null ? (
        <Notice tone="warn" title={M.izleme.ekran.merkezYokBaslik}>
          {central.unavailable} {M.izleme.ekran.merkezYokMetin}
        </Notice>
      ) : null}

      {central.cacheUnavailable !== null ? (
        <Notice tone="warn" title={M.izleme.ekran.onbellekYokBaslik}>
          {central.cacheUnavailable} {M.izleme.ekran.onbellekYokMetin}
        </Notice>
      ) : null}

      {txs.status === "error" ? <ErrorBlock error={txs.error} title={M.izleme.ekran.hareketlerOkunamadi} /> : null}
      {txs.status === "loading" ? <LoadingBlock label={M.izleme.ekran.hareketlerOkunuyor} /> : null}

      {txs.status !== "loading" && trackedIds.length === 0 ? (
        <EmptyState
          title={M.izleme.ekran.aracYokBaslik}
          description={M.izleme.ekran.aracYokMetin}
        />
      ) : null}

      {states.status === "error" ? (
        <ErrorBlock error={states.error} title={M.izleme.ekran.durumOkunamadi} />
      ) : null}
      {states.status === "loading" && trackedIds.length > 0 ? (
        <LoadingBlock label={M.izleme.ekran.durumOkunuyor} />
      ) : null}

      {trackedIds.length > 0 && states.status !== "loading" ? (
        <ul className="flex flex-col gap-3 px-4 pb-4">
          {trackedIds.map((id) => (
            <InstrumentRow
              key={id}
              instrumentId={id}
              entry={(states.data ?? []).find((e) => e.instrumentId === id) ?? null}
            />
          ))}
        </ul>
      ) : null}
    </Screen>
  );
}

/**
 * Bir aracın satırı. `entry === null` "merkez bu aracı hiç görmedi" DEĞİL,
 * "bu cihazın önbelleğinde bu araca ait kayıt yok" demek — ikisini
 * karıştırmamak için metin dikkatli.
 */
function InstrumentRow({
  instrumentId,
  entry,
}: {
  readonly instrumentId: InstrumentId;
  readonly entry: InstrumentCacheEntry | null;
}) {
  if (entry === null) {
    return (
      <li className="rounded-lg border border-line bg-surface p-3">
        <p className="text-[14px] font-semibold text-ink">{instrumentId}</p>
        <p className="pt-1 text-[13px] text-muted">
          {M.izleme.satir.onbellekKaydiYok}
        </p>
      </li>
    );
  }

  const view = describeTrackState(entry.centralState, {
    assetClass: entry.assetClass,
    horizonLimitedBy: entry.horizonLimitedBy,
  });
  const progress = backfillProgress(entry.horizonFrom, entry.backfilledTo, todayIso());
  const title = entry.symbol ?? instrumentId;

  return (
    <li className={cx("rounded-lg border p-3", TONE_CLASS[view.tone])}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-[14px] font-semibold text-ink">
            {title}
            {entry.name !== null && entry.name !== title ? (
              <span className="pl-2 font-normal text-muted">{entry.name}</span>
            ) : null}
          </p>
          <p className="pt-0.5 text-[12px] text-muted">
            {instrumentId}
            {entry.exchange !== null ? ` · ${entry.exchange}` : ""}
            {/* Para birimi BİLİNMİYORSA uydurulmaz: yanlış para birimi sessiz
                değerleme hatasıdır. Kaynak "?" bıraktıysa öyle söylenir. */}
            {entry.currency !== null && entry.currency !== "?"
              ? ` · ${entry.currency}`
              : ` · ${M.izleme.satir.paraBirimiBilinmiyor}`}
          </p>
        </div>
        <Tag>{view.title}</Tag>
      </div>

      <p className="pt-2 text-[13px] text-muted">{view.meaning}</p>

      {view.needsManual ? (
        <p className="pt-2 text-[13px] text-ink">
          <a className="underline" href="#/fiyatlar">
            {M.izleme.satir.elleFiyatBaglanti}
          </a>{" "}
          {M.izleme.satir.elleFiyatSonEk}
        </p>
      ) : null}

      <BackfillLine progress={progress} horizonFrom={entry.horizonFrom} />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 pt-2 text-[12px] text-muted sm:grid-cols-4">
        <Cell label={M.izleme.satir.buCihazda}>
          {entry.cachedFrom === null || entry.cachedTo === null
            ? M.izleme.satir.fiyatYok
            : `${entry.cachedFrom} → ${entry.cachedTo}`}
        </Cell>
        <Cell label={M.izleme.satir.barSayisi}>{entry.barCount.toLocaleString(YEREL)}</Cell>
        <Cell label={M.izleme.satir.merkezde}>
          {entry.centralFrom === null || entry.centralTo === null
            ? M.izleme.satir.bilinmiyor
            : `${entry.centralFrom} → ${entry.centralTo}`}
        </Cell>
        <Cell label={M.izleme.satir.sonEsitleme}>
          {entry.lastSyncedAt === null ? M.izleme.satir.hic : entry.lastSyncedAt.slice(0, 16).replace("T", " ")}
        </Cell>
      </dl>

      {entry.lastError !== null ? (
        <p className="pt-2 text-[12px] text-ink">{M.izleme.satir.sonHata(entry.lastError)}</p>
      ) : null}
    </li>
  );
}

/**
 * Geçmiş ilerlemesi. `bilinmiyor` ve `baslamadi` durumlarında YÜZDE
 * GÖSTERİLMEZ: "%0" ile "ne kadar olduğunu bilmiyoruz" aynı şey değil ve
 * yüzde göstermek ikincisini birincisi gibi okutur.
 */
function BackfillLine({
  progress,
  horizonFrom,
}: {
  readonly progress: ReturnType<typeof backfillProgress>;
  readonly horizonFrom: string | null;
}) {
  if (progress.kind === "bilinmiyor") {
    return (
      <p className="pt-2 text-[13px] text-muted">
        {M.izleme.gecmis.ufukBilinmiyor}
      </p>
    );
  }
  if (progress.kind === "baslamadi") {
    return (
      <p className="pt-2 text-[13px] text-muted">
        {M.izleme.gecmis.baslamadi}
        {horizonFrom === null ? "" : M.izleme.gecmis.ufuk(horizonFrom)}.
      </p>
    );
  }
  if (progress.kind === "tam") {
    return (
      <p className="pt-2 text-[13px] text-muted">
        {M.izleme.gecmis.tam(progress.coveredFrom)}
      </p>
    );
  }
  const percent = Math.round(progress.ratio * 100);
  return (
    <div className="pt-2">
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-line"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label={M.izleme.gecmis.ilerlemeEtiketi}
      >
        <div className="h-full bg-ink/40" style={{ width: `${percent}%` }} />
      </div>
      <p className="pt-1 text-[13px] text-muted">
        {M.izleme.gecmis.kismi(progress.coveredFrom, M.bicim.yuzde(String(percent)))}
      </p>
    </div>
  );
}

function Cell({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-[12px] text-ink">{children}</dd>
    </div>
  );
}

/**
 * Eşitleme düğmesi. Kendi eşitlemesini YAPMIYOR: otomatik eşitlemeyle aynı
 * yoldan geçiyor (`EsitlemeSaglayici`) — iki yol olursa biri kur çekmeyi
 * unutur ya da ikisi aynı anda koşar. Otomatik eşitleme sürerken düğme de
 * "Eşitleniyor…" der.
 *
 * Rapor ÖZETLENİYOR ama "istenmedi" kalemi ayrıca yazılıyor: kalıcı eksiklik
 * (merkezde de yok) ile geçici eksiklik (henüz indirilmedi) kullanıcı için
 * taban tabana zıt iki durum — biri beklemekle geçer, biri geçmez.
 */
function SyncButton({ ids }: { readonly ids: readonly InstrumentId[] }) {
  const esitleme = useEsitleme();
  const canSync = esitleme.kullanilabilir && ids.length > 0;

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        variant="primary"
        disabled={!canSync || esitleme.calisiyor}
        onClick={() => void esitleme.esitle("elle")}
      >
        {esitleme.calisiyor ? M.izleme.esitleme.esitleniyor : M.izleme.esitleme.merkezdenEsitle}
      </Button>
      {esitleme.son !== null ? <SonEsitleme kayit={esitleme.son} /> : null}
    </div>
  );
}

/** Son eşitlemenin zamanı, sebebi ve hataları — sessiz kalmasın (P-a). */
function SonEsitleme({ kayit }: { readonly kayit: EsitlemeKaydi }) {
  const zaman = new Date(kayit.baslangic).toLocaleString(YEREL, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <div className="max-w-md text-right text-[12px] text-muted" aria-label={M.izleme.esitleme.sonEsitleme}>
      <p>
        {M.izleme.esitleme.sonEsitlemeAn(zaman, ESITLEME_SEBEBI_METNI[kayit.sebep])}
        {kayit.kur !== null ? M.izleme.esitleme.kurSatir(kayit.kur.written.toLocaleString(YEREL)) : ""}
      </p>
      {kayit.fiyat !== null ? <SyncSummary report={kayit.fiyat} /> : null}
      {kayit.hatalar.length > 0 ? (
        <p className="pt-1 text-ink">
          {M.izleme.esitleme.hatalar(kayit.hatalar.slice(0, 2).join(" · "))}
          {kayit.hatalar.length > 2 ? M.izleme.esitleme.dahaFazla(kayit.hatalar.length - 2) : ""}
        </p>
      ) : null}
    </div>
  );
}

function SyncSummary({ report }: { readonly report: SyncReport }) {
  const permanent = report.permanentlyMissing.flatMap((p) =>
    p.ranges.map((r) => `${p.instrumentId}: ${r.from}→${r.to}`),
  );
  return (
    <div>
      <p>
        {M.izleme.esitleme.ozet(report.calls.length, report.barsWritten.toLocaleString(YEREL), report.totalMs)}
        {report.upToDate.length > 0 ? M.izleme.esitleme.zatenGuncel(report.upToDate.length) : ""}
      </p>
      {permanent.length > 0 ? (
        <p className="pt-1">
          {M.izleme.esitleme.kaliciEksik(permanent.slice(0, 3).join("; "))}
          {permanent.length > 3 ? M.izleme.esitleme.dahaFazla(permanent.length - 3) : ""}
        </p>
      ) : null}
      {report.rejectedBars > 0 ? (
        <p className="pt-1">{M.izleme.esitleme.reddedildi(report.rejectedBars)}</p>
      ) : null}
    </div>
  );
}

/** Eksik dönem sebeplerinin sözlüğü dışa da açık — Aşama 3 raporları kullanacak. */
export { MISSING_REASON_LABEL };
